import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import type { RequestedSupportingItem } from '@/features/jobs/details/tax-type/info-gathering/RequestedSupportingItem'
import type {
  PersonalTaxRefundMethodSupportingItemInput,
  ProvidedPersonalTaxRefundMethod,
} from '@/features/jobs/details/tax-type/info-gathering/refund-methods/PersonalTaxRefundMethodSupportingItemInput'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { InfoGatheringStore } from '@/features/jobs/details/tax-type/info-gathering/InfoGatheringStore'
import type { TaxRefundMethodVersion } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethod'
import type {
  TaxRefundMethodEditorViewModelFactory,
  FetchPersonalTaxRefundMethodForJobFn,
  ListTaxRefundMethodsFn,
  PromptDeleteTaxRefundMethodFn,
} from '@/features/tax-profile'
import type { TaxRefundMethodEditorViewModel } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethodEditorViewModel'
import { debounceTime, filter, map, merge, Subject, switchMap } from 'rxjs'
import { filterNonNil, withAbortSignal } from '@/utils/rx-util'
import { ignoreAbortError } from '@/utils/abort-util'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { SnackbarRetryWrapperFactory } from '@/features/shell'

/**
 * View model for a personal tax refund method supporting item.
 */
export class PersonalTaxRefundMethodSupportingItemViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * @see {next$}
   * @private
   */
  private readonly _nextSubject = new Subject<unknown>()

  /**
   * Emits when we intend to navigate to the next item.
   */
  readonly next$ = this._nextSubject.asObservable()

  /**
   * Editor VM for the personal tax refund method.
   */
  readonly taxRefundMethodEditorVm: TaxRefundMethodEditorViewModel

  /**
   * The supporting item input type.
   */
  readonly type = 'PersonalTaxRefundMethod' as const

  /**
   * The tax refund method version associated with this item, if any.
   */
  @observable accessor method: TaxRefundMethodVersion | null = null

  /**
   * Tracks whether we're currently creating our first method.
   * Used to conditionally emit on {@link next$}.
   *
   * @private
   */
  private accessor _isCreatingFirstMethod = false

  /**
   * Dialog for showing the tax refund methods list.
   */
  readonly taxRefundMethodsDialog = new ModalViewModel()
  /**
   * Infinite scroll provider for existing tax refund methods in the workspace.
   */
  readonly existingTaxRefundMethodsScroller: InfiniteScrollProvider<TaxRefundMethodVersion>

  constructor(
    private readonly _job: SubmittedJob,
    public readonly item: RequestedSupportingItem<PersonalTaxRefundMethodSupportingItemInput>,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    createTaxRefundMethodEditorViewModel: TaxRefundMethodEditorViewModelFactory,
    private readonly _fetchPersonalTaxRefundMethodForJob: FetchPersonalTaxRefundMethodForJobFn,
    private readonly _listTaxRefundMethods: ListTaxRefundMethodsFn,
    private readonly _promptDeleteTaxRefundMethod: PromptDeleteTaxRefundMethodFn,
  ) {
    this.taxRefundMethodEditorVm = createTaxRefundMethodEditorViewModel({
      refundType: 'Personal',
      isReadOnly: () => this.isReadOnly,
      onSaved: (method) => this.provideMethod(method),
    })

    this.existingTaxRefundMethodsScroller = new InfiniteScrollProvider({
      fetchItems: ({ paging, signal }) =>
        this._listTaxRefundMethods(
          {
            refundType: 'Personal',
            paging,
          },
          signal,
        ),
      limit: 1000,
    })

    // Reload the method when the provided method changes.
    this._lifecycle.manage(
      this.item.input.providedChanges$.pipe(
        debounceTime(200),
        filter((provided: ProvidedPersonalTaxRefundMethod | null) =>
          Boolean(
            provided &&
            !this.method?.equalsIdentity(
              provided.refundMethodId,
              provided.refundMethodVersion,
            ),
          ),
        ),
        switchMap(() => withAbortSignal((signal) => this._fetchCore(signal))),
      ),
    )

    // Remove method from list when delisted
    const delisted$ =
      this.taxRefundMethodEditorVm.taxRefundMethodDelisted$.pipe(
        map((id) =>
          this.existingTaxRefundMethodsScroller.items.find((m) => m.id === id),
        ),
        filterNonNil(),
      )

    // Remove older method versions when a new version is added
    const methodVersionSuperseded$ =
      this.taxRefundMethodEditorVm.taxRefundMethodAdded$.pipe(
        map((newVersion) =>
          this.existingTaxRefundMethodsScroller.items.find(
            (loaded) =>
              loaded.id === newVersion.id &&
              loaded.version !== newVersion.version,
          ),
        ),
        filterNonNil(),
      )

    const methodRemoved$ = merge(methodVersionSuperseded$, delisted$)

    this._lifecycle.manage(
      syncInfiniteScrollerItems({
        scroller: this.existingTaxRefundMethodsScroller,
        additions$: this.taxRefundMethodEditorVm.taxRefundMethodAdded$,
        removals$: methodRemoved$,
      }),
    )
  }

  /**
   * Whether the item is read-only.
   */
  @computed get isReadOnly() {
    return this._job.status === 'Closed'
  }

  /**
   * Whether we're currently fetching the data needed to display the editor.
   */
  @computed get isFetchingEditorData() {
    return (
      this.fetch.pending || this.existingTaxRefundMethodsScroller.fetch.pending
    )
  }

  /**
   * Whether the item is considered completed.
   */
  @computed get isCompleted() {
    return this.item.input.isCompleted
  }

  /**
   * Other available tax refund methods, excluding the current one, based on
   * the version. For example, if version 1 of a method is the current one
   * but version 2 of that same method appears in the list, we'll still
   * show version 2 in the list despite being the same method.
   */
  @computed get otherAvailableMethods() {
    if (!this.method) {
      return this.existingTaxRefundMethodsScroller.items
    }

    return this.existingTaxRefundMethodsScroller.items.filter(
      (m) => !m.equalsIdentity(this.method?.id, this.method?.version),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    this.taxRefundMethodEditorVm.reset(null)
    void this.prepare(this._lifecycle.signal).catch(ignoreAbortError)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.taxRefundMethodsDialog.close()
    this.taxRefundMethodEditorVm.close()
  }

  /**
   * Sets the method.
   */
  @action.bound
  setMethod(method: TaxRefundMethodVersion | null) {
    this.method = method
    this.taxRefundMethodEditorVm.reset(method)
  }

  /**
   * Shows the tax refund methods selection dialog, or the editor if relevant.
   */
  @action.bound
  showTaxRefundMethodsDialog() {
    if (this.isReadOnly) {
      // When read-only with a method, show the editor dialog.
      // This lets the user see which method they chose in a read-only view.
      if (this.method) {
        this.showEditorDialog(this.method)
      }

      return
    }

    // If no existing methods, show the editor directly
    if (this.existingTaxRefundMethodsScroller.items.length === 0) {
      this.showEditorDialog(null)
    } else {
      this.taxRefundMethodsDialog.show()
    }
  }

  /**
   * Shows the editor dialog for creating/editing a method.
   */
  @action.bound
  showEditorDialog(method: TaxRefundMethodVersion | null = null) {
    this.taxRefundMethodEditorVm.showDialog(method)
  }

  /**
   * Prepares the view model by fetching all relevant data.
   *
   * @param signal
   */
  async prepare(signal?: AbortSignal) {
    await Promise.all([
      this.fetch(signal),
      this.existingTaxRefundMethodsScroller.fetch({ signal: signal }),
    ])

    signal?.throwIfAborted()

    // Track whether this is the first method we're creating.
    this._isCreatingFirstMethod =
      this.method === null && this.otherAvailableMethods.length === 0

    // If there are no existing methods and we're not in read-only mode,
    // prepare the editor VM for creating a new method in the wizard context
    if (this._isCreatingFirstMethod && !this.isReadOnly) {
      this.taxRefundMethodEditorVm.reset(null)
    }
  }

  /**
   * Provides a refund method to the job.
   */
  provideMethod = task.resolved(async (method: TaxRefundMethodVersion) => {
    await this._infoGatheringStore.providePersonalTaxRefundMethod(
      this._job.id,
      this.item,
      method,
    )

    this.setMethod(method)

    if (this._isCreatingFirstMethod) {
      this._nextSubject.next(undefined)
      this._isCreatingFirstMethod = false
    }
  })

  /**
   * Prompts for and performs permanent deletion of a tax refund method.
   */
  promptDeleteMethodPermanently = task.resolved(
    async (method: TaxRefundMethodVersion) => {
      // Prevent deletion of the currently selected method
      if (this.method?.id === method.id) {
        return
      }

      const wasDeleted = await this._promptDeleteTaxRefundMethod(
        method,
        this._lifecycle.signal,
      )

      if (wasDeleted) {
        // If the deleted method was the current one, clear it (double-check)
        if (this.method?.id === method.id) {
          this.setMethod(null)
        }
      }
    },
  )

  /**
   * Fetches the method if one has been provided.
   */
  fetch = task.resolved(async (signal?: AbortSignal) => {
    await this._fetchCore(signal)
  })

  /**
   * Core fetch logic that doesn't reset task state.
   * @private
   */
  private async _fetchCore(signal?: AbortSignal) {
    if (!this.item.input.provided) {
      // Underlying item has no provided value, no need to fetch.
      this.setMethod(null)
      return
    }

    const withRetry = this._createSnackbarRetryWrapper(
      'Unable to retrieve personal tax refund method information',
      signal,
    )
    const method = await withRetry(() =>
      this._fetchPersonalTaxRefundMethodForJob(this._job.id, signal),
    )

    signal?.throwIfAborted()
    this.setMethod(method)
  }
}
